/**
 * Emergency Response Planning (ERP) Page
 * Comprehensive emergency management with contacts, checklists, drills, and activations
 */

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import {
  Phone, Mail, Users, AlertTriangle, CheckCircle, Clock, Calendar,
  Plus, Search, Shield, Flame, Heart, Plane, Building, Lock,
  Play, Eye, Edit2, Trash2, Loader2, AlertOctagon, Sparkles,
  ChevronRight, ChevronDown, UserCheck, Siren, FileText, Target
} from 'lucide-react';

// Import extracted tab components
import {
  ERPDashboardTab,
  ERPContactsTab,
  ERPChecklistsTab,
  ERPDrillsTab,
  ERPActivationsTab,
  EMERGENCY_TYPES
} from '../components/erp';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function EmergencyResponsePage() {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [checklists, setChecklists] = useState([]);
  const [drills, setDrills] = useState([]);
  const [activations, setActivations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Dialog states
  const [showContactDialog, setShowContactDialog] = useState(false);
  const [showDrillDialog, setShowDrillDialog] = useState(false);
  const [showActivateDialog, setShowActivateDialog] = useState(false);
  const [showChecklistDialog, setShowChecklistDialog] = useState(false);
  const [showDrillViewDialog, setShowDrillViewDialog] = useState(false);
  const [selectedChecklist, setSelectedChecklist] = useState(null);
  const [selectedDrill, setSelectedDrill] = useState(null);
  const [editingDrill, setEditingDrill] = useState(false);
  
  // Form states
  const [contactForm, setContactForm] = useState({
    name: '', title: '', organization: '', contact_type: 'internal',
    category: 'operations', primary_phone: '', mobile: '', email: '',
    role_in_erp: '', availability: '24/7', is_primary: false
  });
  
  const [drillForm, setDrillForm] = useState({
    name: '', description: '', drill_type: 'tabletop', emergency_type: 'fire',
    scheduled_date: '', location: '', objectives: ''
  });
  
  const [activateForm, setActivateForm] = useState({
    emergency_type: 'aircraft_incident', response_level: 'level_1',
    title: '', description: '', location: ''
  });

  const fetchData = useCallback(async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [statsRes, contactsRes, checklistsRes, drillsRes, activationsRes] = await Promise.all([
        fetch(`${API_URL}/api/erp/stats/summary`, { headers }),
        fetch(`${API_URL}/api/erp/contacts`, { headers }),
        fetch(`${API_URL}/api/erp/checklists`, { headers }),
        fetch(`${API_URL}/api/erp/drills?limit=20`, { headers }),
        fetch(`${API_URL}/api/erp/activations?limit=20`, { headers })
      ]);
      
      if (statsRes.ok) setStats(await statsRes.json());
      if (contactsRes.ok) {
        const data = await contactsRes.json();
        setContacts(data.contacts || []);
      }
      if (checklistsRes.ok) {
        const data = await checklistsRes.json();
        setChecklists(data.checklists || []);
      }
      if (drillsRes.ok) {
        const data = await drillsRes.json();
        setDrills(data.drills || []);
      }
      if (activationsRes.ok) {
        const data = await activationsRes.json();
        setActivations(data.activations || []);
      }
      
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load ERP data');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const seedDefaults = async () => {
    try {
      const res = await fetch(`${API_URL}/api/erp/checklists/seed-defaults`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        fetchData();
      }
    } catch (error) {
      toast.error('Error seeding checklists');
    }
  };

  const createContact = async () => {
    try {
      const res = await fetch(`${API_URL}/api/erp/contacts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(contactForm)
      });
      
      if (res.ok) {
        toast.success('Contact created');
        setShowContactDialog(false);
        setContactForm({
          name: '', title: '', organization: '', contact_type: 'internal',
          category: 'operations', primary_phone: '', mobile: '', email: '',
          role_in_erp: '', availability: '24/7', is_primary: false
        });
        fetchData();
      } else {
        toast.error('Failed to create contact');
      }
    } catch (error) {
      toast.error('Error creating contact');
    }
  };

  const scheduleDrill = async () => {
    try {
      const objectives = drillForm.objectives.split('\n').filter(o => o.trim());
      
      const res = await fetch(`${API_URL}/api/erp/drills`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          ...drillForm,
          objectives
        })
      });
      
      if (res.ok) {
        toast.success('Drill scheduled');
        setShowDrillDialog(false);
        setDrillForm({
          name: '', description: '', drill_type: 'tabletop', emergency_type: 'fire',
          scheduled_date: '', location: '', objectives: ''
        });
        fetchData();
      }
    } catch (error) {
      toast.error('Error scheduling drill');
    }
  };

  const viewDrill = (drill) => {
    setSelectedDrill(drill);
    setEditingDrill(false);
    setShowDrillViewDialog(true);
  };

  const editDrill = (drill) => {
    setSelectedDrill(drill);
    setDrillForm({
      name: drill.name || '',
      description: drill.description || '',
      drill_type: drill.drill_type || 'tabletop',
      emergency_type: drill.emergency_type || 'fire',
      scheduled_date: drill.scheduled_date || '',
      location: drill.location || '',
      objectives: (drill.objectives || []).join('\n')
    });
    setEditingDrill(true);
    setShowDrillViewDialog(true);
  };

  const updateDrill = async () => {
    if (!selectedDrill) return;
    
    try {
      const objectives = drillForm.objectives.split('\n').filter(o => o.trim());
      
      const res = await fetch(`${API_URL}/api/erp/drills/${selectedDrill.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          ...drillForm,
          objectives
        })
      });
      
      if (res.ok) {
        toast.success('Drill updated');
        setShowDrillViewDialog(false);
        setSelectedDrill(null);
        setEditingDrill(false);
        fetchData();
      } else {
        toast.error('Failed to update drill');
      }
    } catch (error) {
      toast.error('Error updating drill');
    }
  };

  const deleteDrill = async (drillId) => {
    if (!confirm('Are you sure you want to delete this drill?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/erp/drills/${drillId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Drill deleted');
        setShowDrillViewDialog(false);
        setSelectedDrill(null);
        fetchData();
      } else {
        toast.error('Failed to delete drill');
      }
    } catch (error) {
      toast.error('Error deleting drill');
    }
  };

  const completeDrill = async (drillId, score, findings, lessonsLearned) => {
    try {
      const params = new URLSearchParams();
      if (score) params.append('score', score);
      if (lessonsLearned) params.append('lessons_learned', lessonsLearned);
      findings.forEach(f => params.append('findings', f));
      
      const res = await fetch(`${API_URL}/api/erp/drills/${drillId}/complete?${params}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Drill marked as completed');
        setShowDrillViewDialog(false);
        fetchData();
      }
    } catch (error) {
      toast.error('Error completing drill');
    }
  };

  const activateEmergency = async () => {
    try {
      const params = new URLSearchParams({
        emergency_type: activateForm.emergency_type,
        response_level: activateForm.response_level,
        title: activateForm.title,
        description: activateForm.description
      });
      if (activateForm.location) params.append('location', activateForm.location);
      
      const res = await fetch(`${API_URL}/api/erp/activations?${params}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Emergency activated!', { description: 'Response team notified' });
        setShowActivateDialog(false);
        fetchData();
      }
    } catch (error) {
      toast.error('Error activating emergency');
    }
  };

  const resolveEmergency = async (activationId) => {
    try {
      const res = await fetch(`${API_URL}/api/erp/activations/${activationId}/resolve`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Emergency resolved');
        fetchData();
      }
    } catch (error) {
      toast.error('Error resolving emergency');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-red-500" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="erp-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <Siren className="w-8 h-8 text-red-500" />
            {t('emergency.title')}
          </h1>
          <p className="text-slate-400 mt-1">{t('emergency.subtitle')}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={seedDefaults}>
            <Sparkles className="w-4 h-4 mr-2" /> Seed Checklists
          </Button>
          <Button onClick={() => setShowActivateDialog(true)} className="bg-red-600 hover:bg-red-700">
            <Siren className="w-4 h-4 mr-2" /> {t('emergency.activate')}
          </Button>
        </div>
      </div>

      {/* Active Emergency Alert */}
      {stats?.emergencies?.active > 0 && (
        <Card className="bg-red-500/20 border-red-500 animate-pulse">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Siren className="w-6 h-6 text-red-400" />
                <div>
                  <h3 className="font-bold text-red-300">ACTIVE EMERGENCY</h3>
                  <p className="text-sm text-red-200">{stats.emergencies.active} emergency response(s) in progress</p>
                </div>
              </div>
              <Button variant="outline" className="border-red-500 text-red-300" onClick={() => setActiveTab('activations')}>
                View Details
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Emergency Contacts</p>
                <p className="text-2xl font-bold text-white">{stats?.contacts?.total || 0}</p>
                <p className="text-xs text-slate-500">{stats?.contacts?.internal || 0} internal, {stats?.contacts?.external || 0} external</p>
              </div>
              <Users className="w-10 h-10 text-blue-500/30" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Response Checklists</p>
                <p className="text-2xl font-bold text-white">{stats?.checklists || 0}</p>
                <p className="text-xs text-slate-500">Procedures documented</p>
              </div>
              <FileText className="w-10 h-10 text-emerald-500/30" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Scheduled Drills</p>
                <p className="text-2xl font-bold text-white">{stats?.drills?.scheduled || 0}</p>
                <p className="text-xs text-slate-500">{stats?.drills?.completed || 0} completed</p>
              </div>
              <Target className="w-10 h-10 text-purple-500/30" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Historical Events</p>
                <p className="text-2xl font-bold text-white">{stats?.emergencies?.total_historical || 0}</p>
                <p className="text-xs text-slate-500">{stats?.emergencies?.active || 0} active</p>
              </div>
              <AlertTriangle className="w-10 h-10 text-amber-500/30" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-800/50 border border-slate-700">
          <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="contacts">Contacts</TabsTrigger>
          <TabsTrigger value="checklists">Checklists</TabsTrigger>
          <TabsTrigger value="drills">Drills</TabsTrigger>
          <TabsTrigger value="activations">Activations</TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-4">
          <ERPDashboardTab
            stats={stats}
            drills={drills}
            checklists={checklists}
            onShowContactDialog={() => setShowContactDialog(true)}
            onShowDrillDialog={() => setShowDrillDialog(true)}
            onShowActivateDialog={() => setShowActivateDialog(true)}
            onNavigateToChecklists={() => setActiveTab('checklists')}
            onSelectChecklist={(checklist) => {
              setSelectedChecklist(checklist);
              setShowChecklistDialog(true);
            }}
          />
        </TabsContent>

        {/* Contacts Tab */}
        <TabsContent value="contacts" className="space-y-4">
          <ERPContactsTab
            contacts={contacts}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            onShowContactDialog={() => setShowContactDialog(true)}
          />
        </TabsContent>

        {/* Checklists Tab */}
        <TabsContent value="checklists" className="space-y-4">
          <ERPChecklistsTab
            checklists={checklists}
            onSelectChecklist={(checklist) => {
              setSelectedChecklist(checklist);
              setShowChecklistDialog(true);
            }}
            onSeedDefaults={seedDefaults}
          />
        </TabsContent>

        {/* Drills Tab */}
        <TabsContent value="drills" className="space-y-4">
          <ERPDrillsTab
            drills={drills}
            onShowDrillDialog={() => setShowDrillDialog(true)}
            onViewDrill={viewDrill}
            onEditDrill={editDrill}
            onDeleteDrill={deleteDrill}
          />
        </TabsContent>

        {/* Activations Tab */}
        <TabsContent value="activations" className="space-y-4">
          <ERPActivationsTab
            activations={activations}
            onResolveEmergency={resolveEmergency}
          />
        </TabsContent>
      </Tabs>

      {/* Add Contact Dialog */}
      <Dialog open={showContactDialog} onOpenChange={setShowContactDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Emergency Contact</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Contact Type</Label>
                <Select value={contactForm.contact_type} onValueChange={(v) => setContactForm(prev => ({ ...prev, contact_type: v }))}>
                  <SelectTrigger className="bg-slate-800 border-slate-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="internal">Internal</SelectItem>
                    <SelectItem value="external">External</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={contactForm.category} onValueChange={(v) => setContactForm(prev => ({ ...prev, category: v }))}>
                  <SelectTrigger className="bg-slate-800 border-slate-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    {contactForm.contact_type === 'internal' ? (
                      <>
                        <SelectItem value="management">Management</SelectItem>
                        <SelectItem value="operations">Operations</SelectItem>
                        <SelectItem value="safety">Safety</SelectItem>
                        <SelectItem value="security">Security</SelectItem>
                        <SelectItem value="medical">Medical</SelectItem>
                      </>
                    ) : (
                      <>
                        <SelectItem value="police">Police</SelectItem>
                        <SelectItem value="fire_department">Fire Department</SelectItem>
                        <SelectItem value="ambulance">Ambulance</SelectItem>
                        <SelectItem value="hospital">Hospital</SelectItem>
                        <SelectItem value="aviation_authority">Aviation Authority</SelectItem>
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Name</Label>
              <Input value={contactForm.name} onChange={(e) => setContactForm(prev => ({ ...prev, name: e.target.value }))} className="bg-slate-800 border-slate-700" />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Title/Role</Label>
                <Input value={contactForm.title} onChange={(e) => setContactForm(prev => ({ ...prev, title: e.target.value }))} className="bg-slate-800 border-slate-700" />
              </div>
              <div className="space-y-2">
                <Label>Organization</Label>
                <Input value={contactForm.organization} onChange={(e) => setContactForm(prev => ({ ...prev, organization: e.target.value }))} className="bg-slate-800 border-slate-700" />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Primary Phone</Label>
                <Input value={contactForm.primary_phone} onChange={(e) => setContactForm(prev => ({ ...prev, primary_phone: e.target.value }))} className="bg-slate-800 border-slate-700" />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input type="email" value={contactForm.email} onChange={(e) => setContactForm(prev => ({ ...prev, email: e.target.value }))} className="bg-slate-800 border-slate-700" />
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Checkbox checked={contactForm.is_primary} onCheckedChange={(checked) => setContactForm(prev => ({ ...prev, is_primary: checked }))} />
              <Label>Primary contact for this category</Label>
            </div>
          </div>
          
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowContactDialog(false)}>Cancel</Button>
            <Button onClick={createContact} className="bg-blue-600 hover:bg-blue-700">Add Contact</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Schedule Drill Dialog */}
      <Dialog open={showDrillDialog} onOpenChange={setShowDrillDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle>Schedule Emergency Drill</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Drill Name</Label>
              <Input value={drillForm.name} onChange={(e) => setDrillForm(prev => ({ ...prev, name: e.target.value }))} className="bg-slate-800 border-slate-700" placeholder="e.g., Q1 Fire Evacuation Drill" />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Drill Type</Label>
                <Select value={drillForm.drill_type} onValueChange={(v) => setDrillForm(prev => ({ ...prev, drill_type: v }))}>
                  <SelectTrigger className="bg-slate-800 border-slate-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="tabletop">Tabletop Exercise</SelectItem>
                    <SelectItem value="functional">Functional Exercise</SelectItem>
                    <SelectItem value="full_scale">Full-Scale Exercise</SelectItem>
                    <SelectItem value="notification">Notification Drill</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Emergency Type</Label>
                <Select value={drillForm.emergency_type} onValueChange={(v) => setDrillForm(prev => ({ ...prev, emergency_type: v }))}>
                  <SelectTrigger className="bg-slate-800 border-slate-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="aircraft_accident">Aircraft Accident</SelectItem>
                    <SelectItem value="fire">Fire</SelectItem>
                    <SelectItem value="medical_emergency">Medical Emergency</SelectItem>
                    <SelectItem value="security_threat">Security Threat</SelectItem>
                    <SelectItem value="hijack">Hijack</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Scheduled Date</Label>
                <Input type="date" value={drillForm.scheduled_date} onChange={(e) => setDrillForm(prev => ({ ...prev, scheduled_date: e.target.value }))} className="bg-slate-800 border-slate-700" />
              </div>
              <div className="space-y-2">
                <Label>Location</Label>
                <Input value={drillForm.location} onChange={(e) => setDrillForm(prev => ({ ...prev, location: e.target.value }))} className="bg-slate-800 border-slate-700" placeholder="e.g., Main Hangar" />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea value={drillForm.description} onChange={(e) => setDrillForm(prev => ({ ...prev, description: e.target.value }))} className="bg-slate-800 border-slate-700" placeholder="Drill scenario and scope..." />
            </div>
            
            <div className="space-y-2">
              <Label>Objectives (one per line)</Label>
              <Textarea value={drillForm.objectives} onChange={(e) => setDrillForm(prev => ({ ...prev, objectives: e.target.value }))} className="bg-slate-800 border-slate-700" placeholder="Test evacuation time&#10;Verify communication systems&#10;Assess team coordination" />
            </div>
          </div>
          
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowDrillDialog(false)}>Cancel</Button>
            <Button onClick={scheduleDrill} className="bg-purple-600 hover:bg-purple-700">Schedule Drill</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Activate Emergency Dialog */}
      <Dialog open={showActivateDialog} onOpenChange={setShowActivateDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-red-400 flex items-center gap-2">
              <Siren className="w-5 h-5" /> Activate Emergency Response
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              This will notify all relevant personnel and begin emergency procedures
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Emergency Type</Label>
                <Select value={activateForm.emergency_type} onValueChange={(v) => setActivateForm(prev => ({ ...prev, emergency_type: v }))}>
                  <SelectTrigger className="bg-slate-800 border-slate-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="aircraft_accident">Aircraft Accident</SelectItem>
                    <SelectItem value="aircraft_incident">Aircraft Incident</SelectItem>
                    <SelectItem value="fire">Fire</SelectItem>
                    <SelectItem value="medical_emergency">Medical Emergency</SelectItem>
                    <SelectItem value="security_threat">Security Threat</SelectItem>
                    <SelectItem value="bomb_threat">Bomb Threat</SelectItem>
                    <SelectItem value="hijack">Hijack</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Response Level</Label>
                <Select value={activateForm.response_level} onValueChange={(v) => setActivateForm(prev => ({ ...prev, response_level: v }))}>
                  <SelectTrigger className="bg-slate-800 border-slate-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="level_1">Level 1 - Local Response</SelectItem>
                    <SelectItem value="level_2">Level 2 - Regional Response</SelectItem>
                    <SelectItem value="level_3">Level 3 - Full Emergency</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Emergency Title</Label>
              <Input value={activateForm.title} onChange={(e) => setActivateForm(prev => ({ ...prev, title: e.target.value }))} className="bg-slate-800 border-slate-700" placeholder="Brief title for the emergency" />
            </div>
            
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea value={activateForm.description} onChange={(e) => setActivateForm(prev => ({ ...prev, description: e.target.value }))} className="bg-slate-800 border-slate-700" placeholder="What happened? Current situation..." />
            </div>
            
            <div className="space-y-2">
              <Label>Location</Label>
              <Input value={activateForm.location} onChange={(e) => setActivateForm(prev => ({ ...prev, location: e.target.value }))} className="bg-slate-800 border-slate-700" placeholder="Where is the emergency?" />
            </div>
          </div>
          
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowActivateDialog(false)}>Cancel</Button>
            <Button onClick={activateEmergency} className="bg-red-600 hover:bg-red-700">
              <Siren className="w-4 h-4 mr-2" /> ACTIVATE EMERGENCY
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Checklist View Dialog */}
      <Dialog open={showChecklistDialog} onOpenChange={setShowChecklistDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-2xl max-h-[80vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedChecklist && (() => {
                const config = EMERGENCY_TYPES[selectedChecklist.emergency_type] || EMERGENCY_TYPES.other;
                return <config.icon className={`w-5 h-5 text-${config.color}-400`} />;
              })()}
              {selectedChecklist?.name}
            </DialogTitle>
            <DialogDescription>{selectedChecklist?.description}</DialogDescription>
          </DialogHeader>
          
          <ScrollArea className="h-[50vh] pr-4">
            <div className="space-y-3">
              {selectedChecklist?.items?.map((item, idx) => (
                <div key={idx} className={`p-3 rounded-lg ${item.is_critical ? 'bg-red-500/10 border border-red-500/30' : 'bg-slate-800/50'}`}>
                  <div className="flex items-start gap-3">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${item.is_critical ? 'bg-red-500/30 text-red-300' : 'bg-blue-500/30 text-blue-300'}`}>
                      {item.order}
                    </div>
                    <div className="flex-1">
                      <p className="text-white font-medium">{item.action}</p>
                      <div className="flex gap-4 mt-1 text-xs text-slate-400">
                        {item.responsible_role && <span>Responsible: {item.responsible_role}</span>}
                        {item.time_frame && <span>Time: {item.time_frame}</span>}
                      </div>
                    </div>
                    {item.is_critical && <Badge className="bg-red-500/20 text-red-300 text-xs">Critical</Badge>}
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
          
          <div className="flex justify-between items-center pt-4 border-t border-slate-700">
            <span className="text-sm text-slate-400">
              {selectedChecklist?.items?.length || 0} steps | {selectedChecklist?.response_level?.replace('_', ' ')}
            </span>
            <Button variant="outline" onClick={() => setShowChecklistDialog(false)}>Close</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Drill View/Edit Dialog */}
      <Dialog open={showDrillViewDialog} onOpenChange={(open) => {
        setShowDrillViewDialog(open);
        if (!open) {
          setSelectedDrill(null);
          setEditingDrill(false);
        }
      }}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedDrill && (() => {
                const config = EMERGENCY_TYPES[selectedDrill.emergency_type] || EMERGENCY_TYPES.other;
                return <config.icon className={`w-5 h-5 text-${config.color}-400`} />;
              })()}
              {editingDrill ? 'Edit Drill' : 'Drill Details'}
            </DialogTitle>
          </DialogHeader>
          
          {editingDrill ? (
            // Edit Mode
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Drill Name</Label>
                <Input value={drillForm.name} onChange={(e) => setDrillForm(prev => ({ ...prev, name: e.target.value }))} className="bg-slate-800 border-slate-700" />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Drill Type</Label>
                  <Select value={drillForm.drill_type} onValueChange={(v) => setDrillForm(prev => ({ ...prev, drill_type: v }))}>
                    <SelectTrigger className="bg-slate-800 border-slate-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      <SelectItem value="tabletop">Tabletop Exercise</SelectItem>
                      <SelectItem value="functional">Functional Exercise</SelectItem>
                      <SelectItem value="full_scale">Full-Scale Exercise</SelectItem>
                      <SelectItem value="notification">Notification Drill</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Emergency Type</Label>
                  <Select value={drillForm.emergency_type} onValueChange={(v) => setDrillForm(prev => ({ ...prev, emergency_type: v }))}>
                    <SelectTrigger className="bg-slate-800 border-slate-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      <SelectItem value="aircraft_accident">Aircraft Accident</SelectItem>
                      <SelectItem value="fire">Fire</SelectItem>
                      <SelectItem value="medical_emergency">Medical Emergency</SelectItem>
                      <SelectItem value="security_threat">Security Threat</SelectItem>
                      <SelectItem value="hijack">Hijack</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Scheduled Date</Label>
                  <Input type="date" value={drillForm.scheduled_date} onChange={(e) => setDrillForm(prev => ({ ...prev, scheduled_date: e.target.value }))} className="bg-slate-800 border-slate-700" />
                </div>
                <div className="space-y-2">
                  <Label>Location</Label>
                  <Input value={drillForm.location} onChange={(e) => setDrillForm(prev => ({ ...prev, location: e.target.value }))} className="bg-slate-800 border-slate-700" />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea value={drillForm.description} onChange={(e) => setDrillForm(prev => ({ ...prev, description: e.target.value }))} className="bg-slate-800 border-slate-700" />
              </div>
              
              <div className="space-y-2">
                <Label>Objectives (one per line)</Label>
                <Textarea value={drillForm.objectives} onChange={(e) => setDrillForm(prev => ({ ...prev, objectives: e.target.value }))} className="bg-slate-800 border-slate-700" />
              </div>
              
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setEditingDrill(false)}>Cancel</Button>
                <Button onClick={updateDrill} className="bg-blue-600 hover:bg-blue-700">Save Changes</Button>
              </div>
            </div>
          ) : (
            // View Mode
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-400">Name</p>
                  <p className="text-white font-medium">{selectedDrill?.name}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-400">Status</p>
                  <Badge className={
                    selectedDrill?.status === 'completed' ? 'bg-emerald-500/20 text-emerald-300' :
                    selectedDrill?.status === 'scheduled' ? 'bg-blue-500/20 text-blue-300' :
                    'bg-slate-500/20 text-slate-300'
                  }>
                    {selectedDrill?.status}
                  </Badge>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-400">Drill Type</p>
                  <p className="text-white capitalize">{selectedDrill?.drill_type?.replace('_', ' ')}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-400">Emergency Type</p>
                  <p className="text-white capitalize">{selectedDrill?.emergency_type?.replace('_', ' ')}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-400">Scheduled Date</p>
                  <p className="text-white">{selectedDrill?.scheduled_date}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-400">Location</p>
                  <p className="text-white">{selectedDrill?.location || '-'}</p>
                </div>
              </div>
              
              {selectedDrill?.description && (
                <div>
                  <p className="text-sm text-slate-400">Description</p>
                  <p className="text-white">{selectedDrill.description}</p>
                </div>
              )}
              
              {selectedDrill?.objectives?.length > 0 && (
                <div>
                  <p className="text-sm text-slate-400 mb-2">Objectives</p>
                  <ul className="list-disc list-inside text-white space-y-1">
                    {selectedDrill.objectives.map((obj, idx) => (
                      <li key={idx}>{obj}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              {selectedDrill?.status === 'completed' && (
                <>
                  {selectedDrill.score && (
                    <div>
                      <p className="text-sm text-slate-400">Score</p>
                      <p className="text-2xl font-bold text-emerald-400">{selectedDrill.score}/100</p>
                    </div>
                  )}
                  {selectedDrill.findings?.length > 0 && (
                    <div>
                      <p className="text-sm text-slate-400 mb-2">Findings</p>
                      <ul className="list-disc list-inside text-white space-y-1">
                        {selectedDrill.findings.map((finding, idx) => (
                          <li key={idx}>{finding}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {selectedDrill.lessons_learned && (
                    <div>
                      <p className="text-sm text-slate-400">Lessons Learned</p>
                      <p className="text-white">{selectedDrill.lessons_learned}</p>
                    </div>
                  )}
                </>
              )}
              
              <div className="flex justify-between items-center pt-4 border-t border-slate-700">
                <div className="flex gap-2">
                  {selectedDrill?.status !== 'completed' && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => editDrill(selectedDrill)}>
                        <Edit2 className="w-4 h-4 mr-2" /> Edit
                      </Button>
                      <Button size="sm" variant="outline" className="text-red-400 border-red-400/50" onClick={() => deleteDrill(selectedDrill?.id)}>
                        <Trash2 className="w-4 h-4 mr-2" /> Delete
                      </Button>
                    </>
                  )}
                </div>
                <Button variant="outline" onClick={() => setShowDrillViewDialog(false)}>Close</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
